import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './AuthContext';
import Layout from './Layout';
import ProtectedRoute from './ProtectedRoute';
import ErrorBoundary from './ErrorBoundary';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Public routes
import LandingPage from './LandingPage';
import Login from './Login';
import Register from './Register';
import PasswordRecovery from './PasswordRecovery';
import PasswordReset from './PasswordReset';
import RegistrationSuccess from './RegistrationSuccess';
import EmploymentApplicationForm from './EmploymentApplicationForm';
import Unauthorized from './Unauthorized';

// Protected routes
import AdminDashboard from './AdminDashboard';
import MainDashboard from './MainDashboard';
import MoneyDashboard from './MoneyDashboard';
import ManagerDashboard from './ManagerDashboard';
import UserDashboard from './UserDashboard';
import WeeklyForecastDashboard from './WeeklyForecastDashboard';
import EmployeeCard from './EmployeeCard';
import ManageApplications from './ManageApplications';
import EmployeeReviews from './EmployeeReviews';
import HolidayCalendar from './HolidayCalendar';
import SubmitHolidayRequest from './SubmitHolidayRequest';
import SubmitAbsence from './SubmitAbsence';
import HolidayApproval from './HolidayApproval';
import SupplierCard from './SupplierCard';
import IncomeCard from './IncomeCard';
import Notes from './Notes';
import PersonalInfo from './PersonalInfo';
import SubmitTraining from './SubmitTraining';
import Disciplinary from './Disciplinary';
import RentalDashboard from './RentalDashboard';
import RentalLayout from './RentalLayout';
import DocumentApproval from './DocumentApproval';
import Reminders from './Reminders';
import MessagesDashboard from './Messages_Dashboard';
import SubmitEmployeeReview from './SubmitEmployeeReview';
import TaxReport from './TaxReport';
import VatDashboard from './VatDashboard';
import VatReturnForm from './VatReturnForm';
import VatReturnView from './VatReturnView';
import CashflowReport from './CashflowReport';
import ProfitLossReport from './ProfitLossReport';
import ReportsDashboard from './ReportsDashboard';
import TransactionReport from './TransactionReport';
import DailyIncomeEntry from './DailyIncomeEntry';
import CommissionDashboard from './CommissionDashboard';
import CommissionTypeEditor from './CommissionTypeEditor';
import EmployeeCommission from './EmployeeCommission';
import PayrollCalculator from './PayrollCalculator';
import ProductManagement from './ProductManagement';
import StockTransactions from './StockTransactions';
import ServiceProductManager from './ServiceProductManager';
import PurchaseOrders from './PurchaseOrders';
import ServiceCalculator from './ServiceCalculator';
import ServiceEditor from './ServiceEditor';
import { 
  IncomeImport, 
  ExpenditureImport, 
  SupplierImport,  
  EmployeeImport,
  TestImport,
  ComingSoon 
} from './imports';

function App() {
  return (
    <Router>
      <ErrorBoundary>
        <AuthProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/password-recovery" element={<PasswordRecovery />} />
              <Route path="/password-reset/:token" element={<PasswordReset />} />
              <Route path="/registration-success" element={<RegistrationSuccess />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/apply/:applicationUrlId" element={<EmploymentApplicationForm />} />
              
              {/* Rental User Route - Separate protected section */}
              <Route 
                path="/rental/*" 
                element={
                  <ProtectedRoute allowedRoles={['RENTAL_USER']}>
                    <RentalLayout>
                      <RentalDashboard />
                    </RentalLayout>
                  </ProtectedRoute>
                }
              />

              {/* CLEAN MAIN DASHBOARD - No Layout wrapper */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <MainDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Redirect /home to clean dashboard */}
              <Route path="/home" element={<Navigate to="/dashboard" replace />} />

              {/* All other protected routes wrapped in Layout */}
              <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                
                {/* Money Dashboard - With layout and back button */}
                <Route
                  path="/money"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <MoneyDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="/forecast-dashboard" element={<WeeklyForecastDashboard />} />
                {/* Backward compatibility routes */}
                <Route
                  path="/financial"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <MoneyDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/owner_dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <MoneyDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Other role-based dashboards */}
                <Route
                  path="/admin_dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ADMIN']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/manager_dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <ManagerDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user_dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                      <UserDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Employee Management - Owner and Manager only */}
                <Route
                  path="/employees"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <EmployeeCard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/employee_card"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <EmployeeCard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/employee_reviews"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <EmployeeReviews />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/submit_employee_review"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <SubmitEmployeeReview />
                    </ProtectedRoute>
                  }
                />

                {/* Time Management - All roles */}
                <Route
                  path="/time-management"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                      <HolidayCalendar />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/holiday_calendar"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                      <HolidayCalendar />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/submit_holiday_request"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                      <SubmitHolidayRequest />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/submit_absence"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                      <SubmitAbsence />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/holiday_approval"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <HolidayApproval />
                    </ProtectedRoute>
                  }
                />

                {/* Financial and Business Management - Owner only */}
                <Route
                  path="/supplier_card"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <SupplierCard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/income"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <IncomeCard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/income_card"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <IncomeCard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calculator"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <ServiceCalculator />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calculator/services/new"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <ServiceEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calculator/services/:serviceId"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <ServiceEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/income/entry"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <DailyIncomeEntry />
                    </ProtectedRoute>
                  }
                />
                
                {/* Stock Management */}
                <Route
                  path="/stock"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <ProductManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stock/products"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <ProductManagement />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stock/transactions"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <StockTransactions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stock/service-products/:serviceId"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <ServiceProductManager />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/stock/purchase-orders"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <PurchaseOrders />
                    </ProtectedRoute>
                  }
                />

                {/* Reports */}
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <ReportsDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <ReportsDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/tax"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <TaxReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/cashflow"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <CashflowReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/profit-loss"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <ProfitLossReport />
                    </ProtectedRoute>
                  }
                />  
                <Route
                  path="/reports/transaction"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <TransactionReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports/vat"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <VatDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vat_dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <VatDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vat-return/:periodId"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <VatReturnForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vat-return/:periodId/view"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <VatReturnView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payroll-calculator"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <PayrollCalculator />
                    </ProtectedRoute>
                  }
                />

                {/* Settings */}
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                      <PersonalInfo />
                    </ProtectedRoute>
                  }
                />

                {/* Data Management Routes */}
                <Route
                  path="/import/test"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <TestImport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/import/income"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <IncomeImport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/import/expenditure"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <ExpenditureImport />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/import/suppliers" 
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <SupplierImport />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/import/employees" 
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <EmployeeImport />
                    </ProtectedRoute>
                  }
                />

                <Route 
                  path="/rental_dashboard" 
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <RentalDashboard />
                    </ProtectedRoute>
                  }
                />
                
                {/* Common Features - All roles */}
                <Route
                  path="/notes"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                      <Notes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reminders"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                      <Reminders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/personal_info"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                      <PersonalInfo />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/messages"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                      <MessagesDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Training and Development */}
                <Route
                  path="/submit_training"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <SubmitTraining />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/employment_applications"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <ManageApplications />
                    </ProtectedRoute>
                  }
                />

                {/* Administrative Features */}
                <Route
                  path="/disciplinary"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <Disciplinary />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/document_approval"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <DocumentApproval />
                    </ProtectedRoute>
                  }
                />
                {/* Commission Management - Owner only */}
                <Route
                  path="/commission/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <CommissionDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/commission/types/new"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <CommissionTypeEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/commission/types/:id"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <CommissionTypeEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/commission/employee/:employeeId"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                      <EmployeeCommission />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Catch-all redirect to landing page */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </LocalizationProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;